/**
 * How much of the app an unlicensed device gets.
 *
 * Three full exports, then the paywall. Editing is never gated: someone has to
 * be able to build the collage they want before being asked to pay for it, and
 * a tool that stops you halfway through the work teaches you to leave.
 *
 * The count is per device and per install. That is a deliberate ceiling on how
 * hard this tries — a determined person can clear their storage — and it is
 * the right trade for a tool with no accounts.
 */

export const FREE_EXPORTS = 3;

export interface QuotaState {
  /** Full exports already spent on this device. */
  used: number;
}

export const emptyQuota: QuotaState = { used: 0 };

/** Read the stored blob back, treating anything unrecognisable as a fresh device. */
export function readQuota(raw: string | null | undefined): QuotaState {
  if (!raw) return emptyQuota;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return emptyQuota;
    const used = (parsed as Record<string, unknown>).used;
    if (typeof used !== 'number' || !Number.isFinite(used) || used < 0) return emptyQuota;
    return { used: Math.floor(used) };
  } catch {
    return emptyQuota;
  }
}

export function writeQuota(quota: QuotaState): string {
  return JSON.stringify({ used: quota.used });
}

/** Exports still free on this device. `Infinity` once a licence is present. */
export function exportsLeft(quota: QuotaState, licensed: boolean): number {
  if (licensed) return Number.POSITIVE_INFINITY;
  return Math.max(0, FREE_EXPORTS - quota.used);
}

export function canExport(quota: QuotaState, licensed: boolean): boolean {
  return licensed || quota.used < FREE_EXPORTS;
}

/** Spend one. A licensed device is not counted, so the number never moves there. */
export function consumeExport(quota: QuotaState, licensed: boolean): QuotaState {
  if (licensed) return quota;
  return { used: quota.used + 1 };
}
